// src/components/PublicSubmit.jsx
import { useState, useEffect, useRef } from "react";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { signInAnonymously } from "firebase/auth";
import { db, auth } from "../firebase";
import { DEFAULT_GUIDELINES, DEFAULT_CATEGORIES } from "../hooks/useOrgSettings";
import { callClaude, MODELS } from "../lib/claudeApi";
import "../styles/index.css";
import "../styles/tokens.css";
import "../styles/components.css";
import PinchIcon from "./PinchIcon";

const SEVERITIES = [
  { value: "low",      label: "Low",      dot: "sev-dot-low",  active: "active-low" },
  { value: "moderate", label: "Moderate", dot: "sev-dot-mod",  active: "active-mod" },
  { value: "critical", label: "Critical", dot: "sev-dot-crit", active: "active-crit" },
];

function getCallable(name) {
  return httpsCallable(getFunctions(getApp()), name);
}

async function moderateAnon(description, category, severity, guidelines) {
  const system = `You are a content moderator for an internal airline operations tool called Pinch Point.
Posts must follow these guidelines:
${guidelines}

Respond ONLY with valid JSON in this exact shape:
{"approved": true|false, "reason": "short reason if rejected"}

Approve if the post is a genuine operational issue described constructively without naming individuals.
Reject if: it names or clearly targets a specific person, is purely a personal complaint with no operational context, contains inappropriate language, or is completely unrelated to airline operations.`;
  const text = await callClaude(system, `Category: ${category}\nSeverity: ${severity}\nDescription: ${description}`, { model: MODELS.INLINE_UX, maxTokens: 800 });
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

async function rewriteAnon(description, category, severity, guidelines) {
  const system = `You are a writing assistant for an internal airline operations tool.
Rewrite the following pinch point to be clear, professional, constructive, and anonymized.
Follow these guidelines:
${guidelines}

Return ONLY the rewritten description text — no preamble, no quotes, no explanation.`;
  return callClaude(system, `Category: ${category}\nSeverity: ${severity}\nDescription: ${description}`, { model: MODELS.INLINE_UX, maxTokens: 800 });
}

export default function PublicSubmit({ orgId: initialOrgId }) {
  const [orgId, setOrgId] = useState(initialOrgId || "");
  const [orgName, setOrgName] = useState("");
  const [orgValid, setOrgValid] = useState(false);
  const [orgLoading, setOrgLoading] = useState(!!initialOrgId);
  const [orgError, setOrgError] = useState("");

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [guidelines, setGuidelines] = useState(DEFAULT_GUIDELINES);
  const [anonEnabled, setAnonEnabled] = useState(true);
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("low");
  const [description, setDescription] = useState("");
  const [shift, setShift] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const debounceRef = useRef(null);
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;

  useEffect(() => {
    if (initialOrgId) validateOrg(initialOrgId);
  }, [initialOrgId]); // eslint-disable-line

  // Debounced AI field suggestions — mirrors PostForm behaviour
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (description.length <= 20 || !orgId) {
      setAiSuggestion(null);
      setAiSuggestLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAiSuggestLoading(true);
      try {
        const fn = getCallable("suggestPostFields");
        const result = await fn({ text: description, orgId });
        const suggestion = result.data;
        setAiSuggestion(suggestion);
        if (suggestion.severity && SEVERITIES.some((s) => s.value === suggestion.severity)) {
          setSeverity(suggestion.severity);
        }
        if (suggestion.category) {
          const match = categoriesRef.current.find(
            (c) => c.toLowerCase() === suggestion.category.toLowerCase()
          );
          if (match) setCategory(match);
        }
      } catch {
        // suggestions are best-effort
      } finally {
        setAiSuggestLoading(false);
      }
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [description, orgId]); // eslint-disable-line

  const validateOrg = async (id) => {
    setOrgLoading(true);
    setOrgError("");
    try {
      if (!auth.currentUser) await signInAnonymously(auth);
      const snap = await getDoc(doc(db, "organizations", id.trim()));
      if (snap.exists()) {
        const data = snap.data();
        setOrgName(data.name);
        if (data.categories?.length) setCategories(data.categories);
        if (data.guidelines) setGuidelines(data.guidelines);
        if (typeof data.anonSubmitEnabled === "boolean") setAnonEnabled(data.anonSubmitEnabled);
        setOrgValid(true);
        setOrgId(id.trim());
      } else {
        setOrgError("Organization not found. Check the ID and try again.");
        setOrgValid(false);
      }
    } catch {
      setOrgError("Could not validate organization.");
      setOrgValid(false);
    } finally {
      setOrgLoading(false);
    }
  };

  const handleRewrite = async () => {
    if (!description.trim()) return;
    setAiLoading(true);
    setError("");
    try {
      const rewritten = await rewriteAnon(description.trim(), category || "General", severity, guidelines);
      setDescription(rewritten.trim());
    } catch {
      setError("AI rewrite unavailable — please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!category) { setError("Please select a category."); return; }
    if (!description.trim()) { setError("Please describe the issue."); return; }

    const COOLDOWN_MS = 2 * 60 * 1000;
    const lastSubmit = parseInt(localStorage.getItem("pp_last_submit") || "0", 10);
    const remaining = Math.ceil((lastSubmit + COOLDOWN_MS - Date.now()) / 1000);
    if (remaining > 0) {
      setError(`Please wait ${remaining}s before submitting again.`);
      return;
    }

    setLoading(true);
    setError("");
    try {
      let verdict;
      try {
        verdict = await moderateAnon(description.trim(), category, severity, guidelines);
      } catch {
        setError("Could not check submission against guidelines — please try again.");
        setLoading(false);
        return;
      }

      if (!verdict.approved) {
        setError(
          "This post does not comply with submission guidelines." +
          (verdict.reason ? ` ${verdict.reason}` : "")
        );
        setLoading(false);
        return;
      }

      await addDoc(collection(db, "organizations", orgId, "pinchpoints"), {
        authorId: null,
        authorName: "Anonymous",
        category,
        shift: shift.trim(),
        severity,
        description: description.trim(),
        upvotes: 0,
        upvotedBy: [],
        anonymous: true,
        timestamp: serverTimestamp(),
        ...(aiSuggestion && {
          aiSuggestedCategory: aiSuggestion.category,
          aiSuggestedSeverity: aiSuggestion.severity,
          aiSuggestionConfidence: aiSuggestion.confidence,
        }),
      });
      localStorage.setItem("pp_last_submit", Date.now().toString());
      setSubmitted(true);
    } catch {
      setError("Submission failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnother = () => {
    setCategory(""); setSeverity("low"); setDescription(""); setShift("");
    setError(""); setSubmitted(false); setAiSuggestion(null);
  };

  const confidencePct = aiSuggestion?.confidence != null
    ? Math.round(aiSuggestion.confidence * 100)
    : null;

  if (submitted) {
    return (
      <div className="public-page">
        <div className="public-card">
          <div className="public-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="auth-title">Thank You</div>
          <div className="public-thankyou-sub">Your pinch point has been submitted to <strong>{orgName}</strong>.</div>
          <p className="public-thankyou-note">
            Your report helps improve operations. It has been submitted anonymously and will be reviewed by your team.
          </p>
          <button className="submit-btn" onClick={handleAnother}>Submit Another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="public-card">
        <div className="public-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div className="auth-title">Pinch Point <PinchIcon /></div>
        <div className="auth-subtitle">Anonymous Report</div>

        {/* ── Org validation ── */}
        {!orgValid && !orgLoading && (
          <div className="public-org-entry">
            <p className="public-org-label">Enter your organization ID to continue</p>
            <div className="public-org-row">
              <input
                type="text"
                value={orgId}
                onChange={(e) => { setOrgId(e.target.value); setOrgError(""); }}
                placeholder="Organization ID"
                onKeyDown={(e) => e.key === "Enter" && orgId.trim() && validateOrg(orgId)}
              />
              <button
                className="submit-btn"
                style={{ flexShrink: 0, padding: "10px 18px" }}
                onClick={() => validateOrg(orgId)}
                disabled={!orgId.trim()}
              >
                Continue
              </button>
            </div>
            {orgError && <div className="auth-error" style={{ marginTop: 8 }}>{orgError}</div>}
          </div>
        )}

        {orgLoading && (
          <div className="public-org-loading">
            <div className="live-dot" style={{ width: 10, height: 10 }} />
            <span>Validating organization…</span>
          </div>
        )}

        {/* ── Disabled state ── */}
        {orgValid && !anonEnabled && (
          <div className="public-disabled">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
            <div className="public-disabled-title">Submissions Disabled</div>
            <div className="public-disabled-sub">Anonymous submissions for <strong>{orgName}</strong> are currently turned off. Please contact your supervisor to report an issue directly.</div>
          </div>
        )}

        {/* ── Submission form ── */}
        {orgValid && anonEnabled && (
          <>
            <div className="public-org-name">{orgName}</div>
            <div className="form-body" style={{ padding: 0 }}>
              <div>
                <label>Shift / Time <span style={{ fontWeight: 400, color: "var(--text-dim)" }}>(optional)</span></label>
                <input
                  type="text"
                  value={shift}
                  onChange={(e) => setShift(e.target.value)}
                  placeholder="e.g. D2 · 1700"
                />
              </div>
              <div>
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); setError(""); }}
                  placeholder="What happened? What was the impact? No names."
                  rows={4}
                />
                <button
                  className="ai-rewrite-btn"
                  onClick={handleRewrite}
                  disabled={aiLoading || !description.trim()}
                  type="button"
                >
                  {aiLoading
                    ? <><span className="ai-spinner" />Rewriting…</>
                    : <><span className="ai-icon">✦</span> AI Suggested Rewrite</>}
                </button>
              </div>
              <div>
                <label>Category</label>
                <select value={category} onChange={(e) => { setCategory(e.target.value); setError(""); }}>
                  <option value="">— Select —</option>
                  {[...categories].sort((a, b) => a.localeCompare(b)).map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label>Severity</label>
                <div className="severity-group">
                  {SEVERITIES.map((s) => (
                    <div
                      key={s.value}
                      className={`sev-label${severity === s.value ? ` ${s.active}` : ""}`}
                      onClick={() => setSeverity(s.value)}
                    >
                      <div className={`sev-dot ${s.dot}`} />
                      {s.label}
                    </div>
                  ))}
                </div>
              </div>
              {(aiSuggestion || aiSuggestLoading) && (
                <div className="ai-suggestion-label">
                  {aiSuggestLoading
                    ? "AI analyzing…"
                    : confidencePct != null
                      ? `AI suggestion · ${confidencePct}% confident`
                      : "AI suggestion applied"}
                </div>
              )}
              {error && <div className="auth-error">{error}</div>}
              <button className="submit-btn" onClick={handleSubmit} disabled={loading || aiLoading}>
                {loading ? "Checking & Submitting…" : "Submit Anonymously"}
              </button>
              <p className="public-anon-note">
                This report is submitted anonymously. No account or login is required.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
